import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, message } from "antd";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { AUTH_TOKEN, ORG_ID, REACT_APP_PAM_API } from "../../../constants";
import { getPlatformUserList } from "../../../service/tenantService";

import { findJumpServer } from "../../../service/sshProxyService";
import { STEPS, DEFAULT_LISTEN_PORT, HEARTBEAT_INTERVAL_SECONDS } from "./sshProxyModel";
import RightRail from "./RightRail";
import Step1JumpHost from "./steps/Step1JumpHost";
import Step2InstallKeys from "./steps/Step2InstallKeys";
import Step3Verify from "./steps/Step3Verify";

// SSH Proxy onboarding wizard. The six-step flow that supersedes the
// Authnull-Proxy-Onboarding prototype, built to the 2026-09-17 UI spec and
// styled with the shared design-system tokens so it sits alongside the AD /
// RADIUS / Database MFA screens under PAM.
const INITIAL = {
  // Step 1
  proxyName: "",
  publicIp: "",
  listenPort: DEFAULT_LISTEN_PORT,
  // Step 2 — all reported by the gateway on its heartbeat and read back from
  // listJumpServer. Null until the gateway reports.
  heartbeatSeen: false,
  lastSeenAt: null,
  proxyVersion: null,
  proxyListen: null,
  lastSeenIp: null,
  hostKeyFingerprint: null,
  // Identity / policy / recording now live on the gateway detail page. Step 3
  // (Verify) still reads operatorKeys and policy for its pre-connect checklist,
  // so the keys stay on the wizard's state — they are just not edited here.
  operatorKeys: [],
  policy: null,
};

// Runs either as its own page (the /pam/jumpServer/onboard route, still live
// for deep links) or embedded in a dialog from the gateway list. Embedded drops
// the page chrome and the right rail: at dialog width the rail would squeeze
// the step content, and everything it reports is already on the gateway page.
// Until the first heartbeat arrives.
const FAST_POLL_MS = 5000;

export default function SshProxyOnboarding({ embedded = false, onDone }) {
  const navigate = useNavigate();
  const [messageApi, ctx] = message.useMessage();

  const [stepIdx, setStepIdx] = useState(0);
  const [state, setState] = useState(INITIAL);
  const [jumpServerId, setJumpServerId] = useState(null);
  const [creating, setCreating] = useState(false);

  const [userOptions, setUserOptions] = useState([]); // value: userId (Step 3)
  const [userEmailOptions, setUserEmailOptions] = useState([]); // value: email (Step 4)

  const patch = (next) => setState((s) => ({ ...s, ...next }));


  // Platform users for Step 3 (User select) and Step 4 (users list).
  useEffect(() => {
    getPlatformUserList({
      payload: { page: 1, pageSize: 500 },
      onSuccess: (res) => {
        const list = res?.data || [];
        const label = (u) => {
          const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
          return name ? `${name} · ${u.email}` : u.email;
        };
        setUserOptions(list.map((u) => ({ value: u.userId, label: label(u) })));
        setUserEmailOptions(list.filter((u) => u.email).map((u) => ({ value: u.email, label: label(u) })));
      },
      onFailure: () => {},
    });
  }, []);

  const created = !!jumpServerId;

  // Poll the jump-server row — the same row the Gateways list shows. What Step 2
  // and Verify need (the first heartbeat, version, listen address, host key
  // fingerprint) only exists once the gateway has started and reported it, so
  // the wizard re-reads every 5 seconds until it has been seen, then drops to
  // the heartbeat cadence. Opening a step re-reads at once, so Verify never
  // shows a value that is up to a minute old.
  //
  // If the create response carried no id, the row is found by its name instead
  // and the id adopted from it.
  useEffect(() => {
    if (!jumpServerId) return undefined;
    let cancelled = false;
    let timer = null;
    const apply = (row) => {
      setState((s) => ({
        ...s,
        lastSeenAt: row.lastSeenAt ?? null,
        lastSeenIp: row.lastSeenIp ?? null,
        proxyVersion: row.proxyVersion ?? null,
        proxyListen: row.proxyListen ?? null,
        hostKeyFingerprint: row.hostKeyFingerprint ?? null,
        heartbeatSeen: !!row.lastSeenAt,
      }));
    };
    const schedule = (seen) => {
      if (cancelled) return;
      timer = setTimeout(read, seen ? HEARTBEAT_INTERVAL_SECONDS * 1000 : FAST_POLL_MS);
    };
    const read = () =>
      findJumpServer({
        jumpServerId: jumpServerId === "created" ? null : jumpServerId,
        serverName: jumpServerId === "created" ? state.proxyName?.trim() : undefined,
        onSuccess: (row) => {
          if (cancelled) return;
          if (!row) return schedule(false);
          if (jumpServerId === "created" && row.id) {
            setJumpServerId(row.id); // restarts this effect with the real id
            return;
          }
          apply(row);
          schedule(!!row.lastSeenAt);
        },
        onFailure: () => schedule(false),
      });
    read();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // stepIdx: re-read on every step change. proxyName is fixed once created.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpServerId, stepIdx]);

  // Step 1 → 2: create the jump-server row. addJumpServer takes exactly
  // serverName, endpoints, publicIpAddress, domains, orgId.
  const createJumpServer = () => {
    if (!state.proxyName?.trim()) return messageApi.error("Gateway name is required");
    if (!state.publicIp?.trim()) return messageApi.error("IP address is required");

    setCreating(true);
    const payload = {
      serverName: state.proxyName.trim(),
      publicIpAddress: state.publicIp.trim(),
      endpoints: [],
      domains: [],
      // No domainId: PR #46 defaults it server-side. No region: it has a DB
      // default and nothing reads it.
      orgId: ORG_ID(),
    };
    axios
      .post(`${REACT_APP_PAM_API}/jumpserver/addJumpServer`, payload, { headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() } })
      .then((res) => {
        setCreating(false);
        // The backend answers { Id, Code, Message }.
        const id = res?.data?.Id || res?.data?.id || res?.data?.jumpServerId || res?.data?.data?.id;
        setJumpServerId(id || "created");
        messageApi.success("Gateway created");
        setStepIdx(1);
      })
      .catch(() => {
        setCreating(false);
        messageApi.error("Couldn't create the gateway");
      });
  };

  const goNext = () => {
    if (stepIdx === 0 && !created) return createJumpServer();
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const goBack = () => setStepIdx((i) => Math.max(i - 1, 0));

  const current = STEPS[stepIdx];

  const stepBody = useMemo(() => {
    switch (current.key) {
      case "jumpHost":
        return <Step1JumpHost state={state} patch={patch} jumpServerId={jumpServerId} />;
      case "install":
        return <Step2InstallKeys state={state} patch={patch} jumpServerId={jumpServerId} />;
      case "finish":
        return <Step3Verify state={state} jumpServerId={jumpServerId} />;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.key, state, userOptions, userEmailOptions, jumpServerId]);

  // Finish only closes the wizard. Status is automatic — the gateway moves
  // itself from IN PROGRESS to ACTIVE on first check-in — so nothing is set here.
  const finish = () => {
    if (onDone) return onDone(created && jumpServerId !== "created" ? jumpServerId : null);
    return navigate(created && jumpServerId !== "created" ? `/pam/jumpServer/${jumpServerId}` : "/pam/jumpServer");
  };

  const shellStyle = embedded
    ? { color: AN_COLORS.ink, fontFamily: AN_FONT }
    : { minHeight: "calc(100vh - 64px)", background: AN_COLORS.bgWarm, color: AN_COLORS.ink, fontFamily: AN_FONT, padding: "22px 28px" };

  return (
    <div style={shellStyle}>
      {ctx}

      {/* Header — the dialog supplies its own title and close affordance. */}
      <div style={{ display: embedded ? "none" : "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, marginBottom: 18 }}>
        <div>
          <div style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 6 }}>
            PAM · Gateway · Onboarding
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4 }}>Onboard a gateway</h1>
          <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4, maxWidth: 640 }}>
            One-time setup: register the gateway, install it on your jump box, then confirm it checks in. Identity, policy, recording and log streaming are settings — they live on the gateway page afterwards.
          </div>
        </div>
        <Button onClick={() => navigate("/pam/jumpServer")} icon={<ArrowLeft size={14} style={{ verticalAlign: -2, marginRight: 6 }} />}>
          Back to Gateways
        </Button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: embedded ? "minmax(0, 1fr)" : "minmax(0, 1fr) 320px", gap: 20, alignItems: "start" }}>
        <div>
          {/* Step rail */}
          <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" }}>
            {STEPS.map((s, i) => {
              const active = i === stepIdx;
              const done = i < stepIdx;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setStepIdx(i)}
                  style={{
                    flex: embedded ? "1 1 0" : "1 1 130px", minWidth: 0, textAlign: "left", cursor: "pointer",
                    background: active ? AN_COLORS.surface : "transparent",
                    border: `1px solid ${active ? AN_COLORS.primary : AN_COLORS.line}`,
                    borderRadius: 9, padding: "9px 12px",
                    boxShadow: active ? `0 0 0 3px ${AN_COLORS.primaryL}` : "none",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10.5, fontWeight: 700, fontFamily: AN_MONO,
                        background: done ? AN_COLORS.success : active ? AN_COLORS.primary : AN_COLORS.line2,
                        color: done || active ? "#fff" : AN_COLORS.muted,
                      }}
                    >
                      {done ? <Check size={12} /> : s.index}
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: active ? AN_COLORS.ink : AN_COLORS.ink2 }}>{s.title}</span>
                  </div>
                  <div style={{ fontSize: 10.5, color: AN_COLORS.muted, marginTop: 3, marginLeft: 28 }}>{s.blurb}</div>
                </button>
              );
            })}
          </div>

          {stepBody}

          {/* Footer nav. Sticky when embedded: the dialog body scrolls, and the
              primary action sitting below the fold is how people conclude a
              step is a dead end. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 20,
              ...(embedded
                ? {
                    position: "sticky",
                    bottom: 0,
                    // The dialog's own colour, so the bar reads as part of it.
                    background: AN_COLORS.elevated,
                    borderTop: `1px solid ${AN_COLORS.line}`,
                    padding: "12px 0 4px",
                  }
                : {}),
            }}
          >
            <Button onClick={goBack} disabled={stepIdx === 0} icon={<ArrowLeft size={14} />}>Back</Button>
            <div style={{ fontSize: 11.5, color: AN_COLORS.muted, fontFamily: AN_MONO }}>
              Step {current.index} of {STEPS.length}
            </div>
            {stepIdx < STEPS.length - 1 ? (
              <Button type="primary" onClick={goNext} loading={creating}>
                {stepIdx === 0 && !created ? "Create & continue" : "Next"}
                <ArrowRight size={14} style={{ verticalAlign: -2, marginLeft: 6 }} />
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<Check size={14} />}
                onClick={finish}
              >
                Finish
              </Button>
            )}
          </div>
        </div>

        {!embedded && <RightRail state={state} jumpServerId={jumpServerId} />}
      </div>
    </div>
  );
}
